import React, { createContext, useContext, useState, ReactNode } from 'react';
import { DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { useDelayedUnsetState } from '../../hooks/useDelayedState';
import { MuiDialog } from '../Dialog/MuiDialog';
import MuiButton from '../Button/Button';

/** Dialog options */
export type DialogOptions = {
  title?: string | null;
  content?: string | null | ReactNode;
  confirmText?: string | null;
  cancelText?: string | null;
  hideCancel?: boolean;
};

/** Dialog result */
export type DialogResult = {
  isConfirmed: boolean;
};

/** Internal state type */
type DialogState = DialogOptions & {
  open: boolean;
  resolve?: (result: DialogResult) => void;
};

/** Context type */
type DialogContextType = {
  fire: (options: DialogOptions) => Promise<DialogResult>;
};

/** Create Dialog Context */
const DialogContext = createContext<DialogContextType | null>(null);

/** Singleton Dialog Service */
class DialogService {
  private static instance: DialogContextType | null = null;

  /** Called from DialogProvider to register the context */
  static register(context: DialogContextType) {
    this.instance = context;
  }

  /** Static Fire Method */
  static async fire(options: DialogOptions): Promise<DialogResult> {
    if (!this.instance) {
      throw new Error('DialogService.fire must be used within a <DialogProvider>.');
    }
    return this.instance.fire(options);
  }
}

/** Dialog Provider Component */
export const DialogProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [_dialog, setDialog] = useState<DialogState>({ open: false });

  const { item: dialog } = useDelayedUnsetState(_dialog, { setCondition: (item) => item.open });

  /** Function to show the dialog and return a Promise */
  const fire = (options: DialogOptions): Promise<DialogResult> =>
    new Promise((resolve) => {
      setDialog({
        ...options,
        open: true,
        resolve,
      });
    });

  /** Handles Confirm */
  const handleConfirm = () => {
    dialog?.resolve?.({ isConfirmed: true });
    setDialog({ open: false });
  };

  /** Handles Cancel */
  const handleCancel = () => {
    dialog?.resolve?.({ isConfirmed: false });
    setDialog({ open: false });
  };

  /** Register singleton service */
  DialogService.register({ fire });

  return (
    <DialogContext.Provider value={{ fire }}>
      {children}
      <MuiDialog open={_dialog.open ?? false} onClose={handleCancel}>
        {dialog?.title && <DialogTitle>{dialog?.title}</DialogTitle>}
        {dialog?.content && <DialogContent>{dialog?.content}</DialogContent>}
        <DialogActions>
          {dialog?.hideCancel !== true && (
            <MuiButton onClick={handleCancel} color="secondary">
              {dialog?.cancelText || 'Cancel'}
            </MuiButton>
          )}
          <MuiButton onClick={handleConfirm} autoFocus>
            {dialog?.confirmText || 'Confirm'}
          </MuiButton>
        </DialogActions>
      </MuiDialog>
    </DialogContext.Provider>
  );
};

/** Hook to use Dialog */
export const useDialog = (): DialogContextType => {
  const context = useContext(DialogContext);
  if (!context) {
    throw new Error('useDialog must be used within a DialogProvider');
  }
  return context;
};

/** Export the Singleton Dialog Service */
export { DialogService };
